import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import FilterByType from './FilterByType';
import Icon from '../Icon/index';
import styles from './Filters.module.less';
import FilterChips from '../../../../website/components/search/SearchFilters/FilterChips';
import { useRouter } from 'next/router';

const Filters = ({
  total,
  viableGrades,
  viableFilters,
  viableContentTypes,
  open,
  onClick,
  closeFilterWrap,
  clearFilters,
  onFilter,
  selectedFilters,
  contentRequestInProcess,
  selectedCategory,
  queries,
  urlPath
}) => {
  const router = useRouter();
  const { asPath } = router;
  const [isAtBottom, setIsAtBottom] = useState(false);
  const filtersBlockRef = useRef(null);

  // get all filters by combining all viable filters, and sort the all filters array by filter rank
  const allFilters = [viableContentTypes, viableGrades, ...viableFilters].sort(
    (filter1, filter2) => {
      return filter1.filterRank - filter2.filterRank;
    }
  );
  const gradeFilterName = viableGrades.name;
  const filters = allFilters.map(filter => (
    <FilterByType
      key={filter.name}
      filterObject={filter}
      onClick={onClick}
      gradeFilterName={gradeFilterName}
      contentRequestInProcess={contentRequestInProcess}
      selectedCategory={selectedCategory}
    />
  ));

  const rootRef = useRef();

  const renderTotalResults = total => {
    if (total === 1) return '1 result';
    // update total number format
    // when value larger than 3 digits, then add ',' to indicate thousands
    const formattedTotal = total
      .toString()
      // get the option of capturing portions of the match and saving them as placeholders '$1' by regex.
      // replace it by itself adding one more character ','
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');

    return `${formattedTotal} results`;
  };

  const handleScroll = () => {
    const filtersBlock = filtersBlockRef.current;
    if (filtersBlock) {
      setIsAtBottom(
        filtersBlock.scrollTop + filtersBlock.clientHeight + 1 >=
          filtersBlock.scrollHeight
      );
    }
  };

  const handleScrollDown = () => {
    if (filtersBlockRef.current) {
      filtersBlockRef.current.scrollBy({
        top: 100,
        behavior: 'smooth',
      });
      handleScroll();
    }
  };

  useEffect(() => {
    // document.addEventListener('mousedown', handleClickTopNavHamburger);
    document.addEventListener('toggleMobileNavMenu', closeFilterWrap);

    // handle whether filters block is scroll down to the end
    const filtersBlock = filtersBlockRef.current;
    if (filtersBlock) {
      filtersBlock.addEventListener('scroll', handleScroll);
    }

    return () => {
      // document.removeEventListener('mousedown', handleClickTopNavHamburger);
      document.addEventListener('toggleMobileNavMenu', closeFilterWrap);

      if (filtersBlock) {
        filtersBlock.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // check whether the filter block has scroll bar, and decide whether to show 'see more' button
  useEffect(() => {
    if (filtersBlockRef.current) {
      const hasScroll =
        filtersBlockRef.current.scrollHeight >
        filtersBlockRef.current.clientHeight;
      setIsAtBottom(!hasScroll);
    }
  }, [asPath, open]);

  // Prevent scrolling on background (body) when mobile component open
  useEffect(() => {
    if (open) {
      // Prevent scrolling on the background (body) when mobile component open
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      // set width to 100% to ensure the TopNav keep the same width as the filter popup when users dynamically change the screen size
      document.body.style.width = '100%';

      // Adjust filter pop up position and height to avoid hiding TopNav
      const header = document.querySelector('header');
      rootRef.current.style.top = `${header.offsetHeight}px`;
      rootRef.current.style.height = `calc(100% - ${header.offsetHeight}px)`;
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.position = '';

      // Unset the scrollTop of the mobile component so that, when you open menu -> scroll -> close menu -> open menu again, we will not have a "saved" scrollTop property
      if (filtersBlockRef.current) {
        filtersBlockRef.current.scrollTop = 0;
      }
    }
  }, [open]);

  const handleClearFilters = e => {
    e.preventDefault();
    clearFilters();
  };

  return (
    <div
      className={clsx(styles.searchFilter, open && styles.open)}
      ref={rootRef}
      role="navigation"
    >
      <div className={styles.filterResultsTitle}>
        <div className={styles.filterResultsTitleText}>Filters</div>
        <Icon
          name="cancel-light"
          className={styles.removeIcon}
          onClick={closeFilterWrap}
        />
      </div>
      <ul ref={filtersBlockRef}>
        <li className={clsx(styles.chipWrapper)}>
          <FilterChips
            clickFilterChip={onFilter}
            handleClearAllClick={handleClearFilters}
            selectedFilters={selectedFilters}
            hasClearAllBtn={false}
            queries={queries}
            urlPath={urlPath}
          />
        </li>
        {filters}
      </ul>
      {!isAtBottom && <div className={clsx(styles.hasMoreBackground)}></div>}
      <div className={clsx(styles.btnWrapper)}>
        {!isAtBottom && (
          <div className={clsx(styles.seeMore)} onClick={handleScrollDown}>
            See More
          </div>
        )}
        <div className={clsx(styles.clearAndViewBtnWrapper)}>
          <button
            className={clsx(styles.clearAllBtn)}
            onClick={handleClearFilters}
          >
            Clear All
          </button>
          <button
            className={clsx(styles.viewTotalResultBtn)}
            onClick={closeFilterWrap}
          >
            View {renderTotalResults(total)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Filters;

Filters.propTypes = {
  total: PropTypes.number.isRequired,
  viableGrades: PropTypes.object,
  viableFilters: PropTypes.array,
  viableContentTypes: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  closeFilterWrap: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  onFilter: PropTypes.func.isRequired,
  selectedFilters: PropTypes.array.isRequired,
  contentRequestInProcess: PropTypes.bool.isRequired,
  selectedCategory: PropTypes.string.isRequired,
  queries: PropTypes.string,
  urlPath: PropTypes.string
};

Filters.defaultProps = {
  viableGrades: {},
  viableFilters: [],
  viableContentTypes: {},
};
